import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * A generic click-toggled dropdown for the header.
 * Mirrors the show/hide + outside-click + Escape behavior of
 * dropdown-localization-component, without any localization-form coupling.
 *
 * Markup contract:
 *   <header-dropdown-component>
 *     <button ref="button" on:click="/toggleSelector" aria-expanded="false">...</button>
 *     <div ref="panel" hidden>...</div>
 *   </header-dropdown-component>
 *
 * @typedef {object} HeaderDropdownRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} panel
 *
 * @extends {Component<HeaderDropdownRefs>}
 */
class HeaderDropdownComponent extends Component {
  get isHidden() {
    return this.refs.panel?.hasAttribute('hidden') ?? true;
  }

  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.refs.panel || !this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button?.setAttribute('aria-expanded', 'true');
  }

  hidePanel = () => {
    if (!this.refs.panel || this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);
  }

  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.button?.focus();
    }
  };
}

if (!customElements.get('header-dropdown-component')) {
  customElements.define('header-dropdown-component', HeaderDropdownComponent);
}
